import {
  Box,
  ButtonVariants,
  FormControlSizes,
  FormGroup,
  getDisplayNameOrNameLabel,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  SearchSelect,
  Text,
  ToggleGroup,
  useCallbackRef,
  useMixpanel,
  usePortal,
  type ToggleGroupOption,
} from '@talos/kyoko';
import { PriceGridMultiSelectFilter } from '../../filters/PriceGridMultiSelectFilter';
import { PriceGridHeaderContainer, PriceGridHeaderFilters } from '../../PriceGridHeaderLayout';
import { usePredictionMarketsPriceGridFilterControls } from './filters';
import { getPredictionMarketsPriceView, setPredictionMarketsPriceView } from './priceView';
import {
  PredictionMarketsPriceView,
  type PredictionMarketsPriceGridFilterState,
  type PredictionMarketsPriceView as PredictionMarketsPriceViewValue,
} from './types';
import type { PredictionMarketsPriceGridMarketSelection } from './usePredictionMarketsPriceGridMarketSelection';

const PRICE_VIEW_OPTIONS: ToggleGroupOption<PredictionMarketsPriceViewValue>[] = [
  {
    value: PredictionMarketsPriceView.Yes,
    displayName: 'Yes',
    tooltip: 'Show prices normalized to the Yes outcome',
  },
  {
    value: PredictionMarketsPriceView.No,
    displayName: 'No',
    tooltip: 'Show prices normalized to the No outcome',
  },
  {
    value: PredictionMarketsPriceView.Both,
    displayName: 'Both',
    tooltip: 'Show Yes and No prices side by side',
  },
];

interface PredictionMarketsPriceGridHeaderProps {
  filtersPortalID: string;
  filterState: PredictionMarketsPriceGridFilterState;
  onFilterStateChanged: (filterState: PredictionMarketsPriceGridFilterState) => void;
  marketSelection: PredictionMarketsPriceGridMarketSelection;
}

export function PredictionMarketsPriceGridHeader({
  filtersPortalID,
  filterState,
  onFilterStateChanged,
  marketSelection,
}: PredictionMarketsPriceGridHeaderProps) {
  const {
    marketOptions,
    selectedMarket,
    showMarketSelector,
    onMarketChanged,
    marketAccountOptions,
    selectedMarketAccount,
    marketAccountSelectorDisabled,
    onMarketAccountChanged,
  } = marketSelection;
  const mixpanel = useMixpanel(MixpanelEventSource.PredictionMarketsPriceGrid);
  const { setPortalRef: filtersPortalRef } = usePortal(filtersPortalID);
  const controls = usePredictionMarketsPriceGridFilterControls({
    filterState,
    onFilterStateChanged,
  });
  const priceView = getPredictionMarketsPriceView(filterState);

  const trackFilterChange = (filter: string, selectionCount: number) => {
    mixpanel.track(MixpanelEvent.FilterColumn, {
      [MixpanelEventProperty.Filter]: filter,
      [MixpanelEventProperty.Value]: selectionCount,
      [MixpanelEventProperty.Source]: MixpanelEventSource.PredictionMarketsPriceGrid,
    });
  };

  const handleCategoriesChange = useCallbackRef((selections: string[]) => {
    trackFilterChange('Category', selections.length);
    controls.categories.onSelectionsChange(selections);
  });

  const handleSeriesChange = useCallbackRef((selections: string[]) => {
    trackFilterChange('Series', selections.length);
    controls.series.onSelectionsChange(selections);
  });

  const handleMarketChange = useCallbackRef((market: PredictionMarketsPriceGridMarketSelection['selectedMarket']) => {
    trackFilterChange('Market', market ? 1 : 0);
    onMarketChanged(market);
  });

  const handleMarketAccountChange = useCallbackRef(
    (marketAccount: PredictionMarketsPriceGridMarketSelection['selectedMarketAccount']) => {
      trackFilterChange('Market Account', marketAccount ? 1 : 0);
      onMarketAccountChanged(marketAccount);
    }
  );

  const handlePriceViewChange = useCallbackRef((nextPriceView: PredictionMarketsPriceViewValue) => {
    onFilterStateChanged(setPredictionMarketsPriceView(filterState, nextPriceView));
  });

  return (
    <PriceGridHeaderContainer data-testid="prediction-markets-price-grid-header">
      <PriceGridHeaderFilters>
        <Text color="colorTextImportant" fontWeight={500} whiteSpace="nowrap">
          Prediction Markets Price Grid
        </Text>
        <ToggleGroup
          data-testid="prediction-markets-price-grid-price-view-toggle"
          options={PRICE_VIEW_OPTIONS}
          value={priceView}
          onChange={handlePriceViewChange}
          size={FormControlSizes.Small}
          defaultSelectedVariant={ButtonVariants.Priority}
        />
        {showMarketSelector ? (
          <FormGroup
            inline
            alignItems="center"
            label="Market"
            gap="spacing4"
            mb="0"
            controlId="PredictionMarketsPriceGridMarket"
          >
            <SearchSelect
              dropdownWidth="220px"
              dropdownPlacement="bottom-start"
              selection={selectedMarket}
              options={marketOptions}
              portalize
              size={FormControlSizes.Small}
              dropdownSize={FormControlSizes.Small}
              getLabel={option => option.label}
              onChange={handleMarketChange}
              data-testid="prediction-markets-price-grid-market-selector"
            />
          </FormGroup>
        ) : null}
        <FormGroup
          inline
          alignItems="center"
          label="Market Account"
          gap="spacing4"
          mb="0"
          controlId="PredictionMarketsPriceGridMarketAccount"
        >
          <SearchSelect
            dropdownWidth="250px"
            dropdownPlacement="bottom-start"
            selection={selectedMarketAccount}
            options={marketAccountOptions}
            disabled={marketAccountSelectorDisabled}
            portalize
            size={FormControlSizes.Small}
            dropdownSize={FormControlSizes.Small}
            getLabel={getDisplayNameOrNameLabel}
            onChange={handleMarketAccountChange}
            data-testid="prediction-markets-price-grid-market-account-selector"
          />
        </FormGroup>
        <PriceGridMultiSelectFilter
          width="140px"
          tooltip="View Events in the selected Categories"
          defaultLabel="All Categories"
          property={controls.categories.property}
          selections={controls.categories.selections}
          onSelectionsChange={handleCategoriesChange}
          buttonTestID="prediction-markets-price-grid-category-filter"
        />
        <PriceGridMultiSelectFilter
          width="160px"
          tooltip="View Events in the selected Series"
          defaultLabel="All Series"
          property={controls.series.property}
          selections={controls.series.selections}
          onSelectionsChange={handleSeriesChange}
          buttonTestID="prediction-markets-price-grid-series-filter"
        />
        <Box ref={filtersPortalRef} />
      </PriceGridHeaderFilters>
    </PriceGridHeaderContainer>
  );
}
